import { createContext, type ReactNode, useContext, useState } from "react";

// Context for managing card expansion state
interface ExpansionContextValue {
    expandedCardId: string | null;
    expandCard: (cardId: string) => void;
    contractCard: () => void;
}

const ExpansionContext = createContext<ExpansionContextValue | null>(null);

// Provider component with grid wrapper
interface ExpansionProviderProps {
    children: ReactNode;
    gridClassName?: string;
}

export const ExpansionProvider = ({
    children,
    gridClassName = "grid grid-cols-2 gap-2",
}: ExpansionProviderProps) => {
    const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

    const expandCard = (cardId: string) => setExpandedCardId(cardId);
    const contractCard = () => setExpandedCardId(null);

    // When expanded, remove grid classes and make container relative for absolute positioning
    const containerClassName = expandedCardId
        ? "relative flex-1"
        : `relative ${gridClassName} flex-1`;

    return (
        <ExpansionContext.Provider
            value={{ expandedCardId, expandCard, contractCard }}
        >
            <div className={containerClassName}>{children}</div>
        </ExpansionContext.Provider>
    );
};

// Hook to consume context
export const useAreaExpansion = () => {
    const context = useContext(ExpansionContext);
    if (!context) {
        throw new Error(
            "useCardExpansion must be used within ExpansionProvider",
        );
    }
    return context;
};

// Props for ExpandableCardWithTabs
interface ExpandableAreaProps {
    content?: ReactNode;
    className?: string;
    expandable?: boolean;
    cardId: string;
    onExpand?: (cardId: string) => void;
    onContract?: () => void;
}

// Main expandable card component
export const ExpandableArea = (props: ExpandableAreaProps) => {
    const { cardId, content } = props;
    const { expandedCardId } = useAreaExpansion();

    const isExpanded = expandedCardId === cardId;
    const isHidden = expandedCardId !== null && !isExpanded;

    return (
        <div
            className={`relative ${isExpanded ? "absolute inset-0 z-10 h-full w-full" : ""} ${isHidden ? "hidden" : ""}`}
        >
            <div className="w-full h-full bg-white rounded-lg border drop-shadow">
                {content}
            </div>
        </div>
    );
};
